import type { EntitySection } from '@/lib/entity-sections';
import { canPerformAct, type Session } from '@/lib/permissions';

/*
 * The courtesy section gate (E02.F09 — R116). UI-free (no JSX): it decides
 * WHICH of a kind's composed sections a person is shown, never whether an act
 * is allowed. The server is the authority and refuses per action (E01 F03b R55,
 * Rich Theil Decision 3); this only WITHHOLDS a section the person would be
 * refused inside, so a section someone can open but not use is not a support
 * call (AC-0e6421c9, AC-73eff17c). A build that only hides is worse than one
 * that only refuses — this is the hide half, layered over the server's refuse.
 *
 * The gate is decided in ONE place (never a role-string compare, AGENTS.md):
 * `canPerformAct` reads the server-sourced capability surface for the act a
 * section's content requires. A section with NO gated act (the profile, the
 * organization's own record surfaces) is always shown — withholding it would
 * hide the organization itself. A section WITH a gated act is shown only when
 * the person holds that act's permission.
 *
 * Fail-closed: a null session is withheld everything but the profile. Inside the
 * `_auth` layer the sign-in guard has already redirected a caller with no
 * session to sign-in (R125 AC-171153b5), so a null session here is the
 * belt-and-braces, not the primary refusal.
 */

/*
 * The permission a section's content requires, keyed by section id, where the
 * section is gated. A section absent from the map is UNGATED — always shown
 * (the profile and the organization's own record surfaces). The values are the
 * capability strings the server-sourced `/me` surface carries; the frontend
 * never invents a rule, it reads what the server grants.
 */
export type SectionActMap = Record<string, string | undefined>;

/*
 * The production section→act map. Every money-path section is ungated today
 * (its owning feature has not yet defined a capability string), so nothing is
 * withheld yet — the server still refuses the underlying read per action. As
 * each money-path feature lands it adds its act here, in this ONE place, and
 * the withhold branch below starts dropping the section for a caller who lacks
 * it. The branch itself is proven today by a synthetic gated map in the test.
 */
const SECTION_ACT: SectionActMap = {
  profile: undefined,
  'bank-accounts': undefined,
};

/*
 * Whether the session may be shown a section (the courtesy gate, R116).
 *
 * `actMap` defaults to the production `SECTION_ACT`; it is a parameter so the
 * withhold branch (a gated section a session lacks the act for → dropped) can
 * be driven and proven even while every production section is ungated. Callers
 * never pass it — production behaviour is exactly the default map.
 */
export function mayUseSection(
  section: EntitySection,
  session: Session | null,
  actMap: SectionActMap = SECTION_ACT,
): boolean {
  const act = actMap[section.id];
  // Ungated section (profile, bank-accounts, any not-yet-gated money section):
  // always shown.
  if (act === undefined) return true;
  // Gated section: shown only when the person holds the act's permission;
  // withheld otherwise (the withhold branch — the heart of R116, AC-73eff17c).
  return canPerformAct(session, act);
}

/** Narrow a kind's composed sections to those the person may use (R116). */
export function gateSectionsForSession(
  sections: readonly EntitySection[],
  session: Session | null,
  actMap: SectionActMap = SECTION_ACT,
): readonly EntitySection[] {
  return sections.filter((s) => mayUseSection(s, session, actMap));
}
